/** Visible objects of one layer, split into the unsorted (opaque) and sorted (transparent) lists. */
export interface HsCullingTarget<TObject> {
  unsortedObjects: TObject[];
  sortedObjects: TObject[];
  clear(): void;
}

/** Fills the target with the queue's objects the camera sees. */
export interface HsCullingFilter<TQueue, TCamera, TTarget> {
  filter(queue: TQueue, camera: TCamera, target: TTarget): void;
}

/** Orders the target's lists for drawing. */
export interface HsCullingSorter<TCamera, TTarget> {
  sort(camera: TCamera, target: TTarget): void;
}

/** Optional timing hooks; `HsViewerProfiler` fits them. A null span is not ended. */
export interface HsCullingSpans<TSpan> {
  startSpan(name: string): TSpan;
  endSpan(span: TSpan): void;
}

/**
 * One frustum culling pass: filter, sort, and compare with the previous frame's lists. When the lists did
 * not change and `reuseUnchangedLists` is set, the previous arrays are put back, so draw batches keyed on
 * them stay valid.
 */
export class HsCullingPass<TObject, TQueue, TCamera, TTarget extends HsCullingTarget<TObject>> {
  public constructor(
    private readonly filter: HsCullingFilter<TQueue, TCamera, TTarget>,
    private readonly sorter: HsCullingSorter<TCamera, TTarget>
  ) {}

  /** Runs the pass; returns whether the visible lists changed. */
  public run<TSpan>(
    queue: TQueue,
    camera: TCamera,
    target: TTarget,
    reuseUnchangedLists: boolean,
    spans: HsCullingSpans<TSpan> | null = null
  ): boolean {
    const previousUnsorted: TObject[] = target.unsortedObjects;
    const previousSorted: TObject[] = target.sortedObjects;
    target.clear();
    const filterSpan: TSpan | null = spans?.startSpan('frustum.filter') ?? null;
    this.filter.filter(queue, camera, target);
    if (filterSpan !== null) {
      spans?.endSpan(filterSpan);
    }
    const sortSpan: TSpan | null = spans?.startSpan('frustum.sort') ?? null;
    this.sorter.sort(camera, target);
    if (sortSpan !== null) {
      spans?.endSpan(sortSpan);
    }
    const compareSpan: TSpan | null = spans?.startSpan('frustum.list-compare') ?? null;
    const changed: boolean =
      previousUnsorted.length !== target.unsortedObjects.length ||
      previousSorted.length !== target.sortedObjects.length ||
      previousUnsorted.some((object: TObject, index: number) => object !== target.unsortedObjects[index]) ||
      previousSorted.some((object: TObject, index: number) => object !== target.sortedObjects[index]);
    if (compareSpan !== null) {
      spans?.endSpan(compareSpan);
    }
    if (!changed && reuseUnchangedLists) {
      target.unsortedObjects = previousUnsorted;
      target.sortedObjects = previousSorted;
    }
    return changed;
  }
}
